import { MOCK_LEDGER_ADJUSTMENTS } from "@/mocks/ledger";
import { listGroups } from "@/services/groups";
import { listMembers } from "@/services/members";
import { buildAllDemands, listLateFeeApplications, listReceipts } from "@/services/collections";
import { listPayouts } from "@/services/payouts";
import { USE_MOCK_API } from "@/services/config";
import { http } from "@/services/http";
import {
  ApiError,
  type CollectionDemand,
  type CollectionReceipt,
  type ConsolidatedGroupSummary,
  type KametiGroup,
  type LedgerEntry,
  type LedgerEntryType,
  type Member,
  type MemberConsolidatedStatement,
  type MemberStatement,
  type MemberStatementGroup,
  type PayoutStatus,
  type PayoutView,
  type StatementCycleRow,
  type StatementPayoutRow,
} from "@/services/types";
import { cycleDate } from "@/utils/collection";
import { cyclePeriodLabel } from "@/utils/group";

/* ================= Real API — backend shapes and mapping ================= */

interface BackendLedgerEntry {
  id: string;
  memberId: string;
  groupId: string;
  groupCode: string;
  groupName: string;
  cycle: number | null;
  periodLabel: string | null;
  date: string;
  type: LedgerEntryType;
  narration: string;
  debit: string;
  credit: string;
  runningBalance: string;
  reference: string;
  reversed: boolean;
}

function mapLedgerEntry(row: BackendLedgerEntry): LedgerEntry {
  return {
    id: row.id,
    memberId: row.memberId,
    groupId: row.groupId,
    groupCode: row.groupCode,
    groupName: row.groupName,
    cycle: row.cycle,
    periodLabel: row.periodLabel,
    date: row.date,
    type: row.type,
    narration: row.narration,
    debit: Number(row.debit),
    credit: Number(row.credit),
    runningBalance: Number(row.runningBalance),
    reference: row.reference,
    reversed: row.reversed,
  };
}

interface BackendStatementCycleRow {
  cycle: number;
  periodLabel: string;
  baseInstallment: string;
  dividendCredit: string;
  commissionPerMember?: string;
  netPayable: string;
  paid: string;
  balance: string;
  paymentDate: string | null;
  receiptNumber: string | null;
  won: boolean;
  isReserved: boolean;
}

interface BackendStatementPayoutRow {
  voucherNumber: string;
  cycle: number;
  periodLabel: string;
  prize: string;
  duesAdjusted: string;
  netReceived: string;
  paidOn: string | null;
  status: PayoutStatus;
}

interface BackendMemberStatementGroup {
  groupId: string;
  groupCode: string;
  groupName: string;
  ticketNumber: string;
  monthlyInstallment: string;
  joinedOn: string;
  wonCycle: number | null;
  wonOn: string | null;
  cycles: BackendStatementCycleRow[];
  payouts: BackendStatementPayoutRow[];
  totalCharged: string;
  totalDividend: string;
  totalCommission?: string;
  totalNetPayable?: string;
  totalPaid: string;
  totalOutstanding: string;
  cyclesRemaining: number;
}

function mapStatementGroup(g: BackendMemberStatementGroup): MemberStatementGroup {
  return {
    groupId: g.groupId,
    groupCode: g.groupCode,
    groupName: g.groupName,
    ticketNumber: g.ticketNumber,
    monthlyInstallment: Number(g.monthlyInstallment),
    joinedOn: g.joinedOn,
    wonCycle: g.wonCycle,
    wonOn: g.wonOn,
    cycles: g.cycles.map((c) => ({
      cycle: c.cycle,
      periodLabel: c.periodLabel,
      baseInstallment: Number(c.baseInstallment),
      dividendCredit: Number(c.dividendCredit),
      commissionPerMember: c.commissionPerMember != null ? Number(c.commissionPerMember) : undefined,
      netPayable: Number(c.netPayable),
      paid: Number(c.paid),
      balance: Number(c.balance),
      paymentDate: c.paymentDate,
      receiptNumber: c.receiptNumber,
      won: c.won,
      isReserved: c.isReserved,
    })),
    payouts: g.payouts.map((p) => ({
      voucherNumber: p.voucherNumber,
      cycle: p.cycle,
      periodLabel: p.periodLabel,
      prize: Number(p.prize),
      duesAdjusted: Number(p.duesAdjusted),
      netReceived: Number(p.netReceived),
      paidOn: p.paidOn,
      status: p.status,
    })),
    totalCharged: Number(g.totalCharged),
    totalDividend: Number(g.totalDividend),
    totalCommission: g.totalCommission != null ? Number(g.totalCommission) : undefined,
    totalNetPayable: g.totalNetPayable != null ? Number(g.totalNetPayable) : undefined,
    totalPaid: Number(g.totalPaid),
    totalOutstanding: Number(g.totalOutstanding),
    cyclesRemaining: g.cyclesRemaining,
  };
}

interface BackendMemberStatement {
  memberId: string;
  memberCode: string;
  memberName: string;
  memberPhone: string;
  city: string;
  generatedAt: string;
  periodLabel: string;
  group: BackendMemberStatementGroup;
}

interface BackendConsolidatedGroupSummary {
  groupId: string;
  groupCode: string;
  groupName: string;
  ticketNumber: string;
  monthlyInstallment: string;
  totalCharged: string;
  totalDividend: string;
  totalPaid: string;
  totalOutstanding: string;
  wonCycle: number | null;
  cyclesRemaining: number;
}

interface BackendMemberConsolidatedStatement {
  memberId: string;
  memberCode: string;
  memberName: string;
  memberPhone: string;
  city: string;
  generatedAt: string;
  groups: BackendConsolidatedGroupSummary[];
  totalOutstanding: string;
  totalDividend: string;
  payoutCount: number;
}

/**
 * Centralized ledger service (Phase 6). Every statement and ledger screen calls
 * through here — no screen performs a direct data fetch.
 *
 * The ledger is an instrument of verification: it derives every entry from the
 * SAME data the auction, collection and payout modules used, so a member's
 * statement reconciles against the group's collection sheet and the payout
 * vouchers. The installment due is always debited at the FULL installment and
 * the dividend credited separately — never a reduced debit plus a dividend,
 * which would count the benefit twice. Payout entries are recorded but kept out
 * of the running contribution balance so a winner's balance does not swing.
 */

const delay = (ms = 300) => new Promise((resolve) => setTimeout(resolve, ms));

/** Per-member dividend credit and net installment for a decided cycle. */
function cycleFinancials(group: KametiGroup, cycle: number): { dividend: number; net: number; wonMemberId: string } | null {
  const won = group.wonCycles.find((w) => w.cycle === cycle);
  if (!won) return null;
  if (cycle === group.reservedCycle) {
    return { dividend: 0, net: group.monthlyInstallment, wonMemberId: won.memberId };
  }
  const discount = Math.max(0, group.chitValue - won.winningBid);
  const dividend = discount > 0 ? Math.floor(discount / group.memberCount) : 0;
  return { dividend, net: group.monthlyInstallment - dividend, wonMemberId: won.memberId };
}

interface LedgerCtx {
  groups: KametiGroup[];
  members: Member[];
  demands: CollectionDemand[];
  payouts: PayoutView[];
  receipts: CollectionReceipt[];
}

async function loadCtx(): Promise<LedgerCtx> {
  const [groups, members, demands, payouts, receipts] = await Promise.all([
    listGroups(),
    listMembers(),
    buildAllDemands(),
    listPayouts(),
    listReceipts(),
  ]);
  return { groups, members, demands, payouts, receipts };
}

const TYPE_RANK: Record<LedgerEntryType, number> = {
  installment_due: 1,
  dividend_credit: 2,
  auction_won: 3,
  payment_received: 4,
  late_fee: 5,
  adjustment: 6,
  payout_disbursed: 7,
};

/** Build the full chronological ledger for one member. */
export async function getMemberLedger(memberId: string): Promise<LedgerEntry[]> {
  if (!USE_MOCK_API) {
    const entries = await http<BackendLedgerEntry[]>(`/ledger/${memberId}`, { method: "GET" });
    return entries.map(mapLedgerEntry);
  }

  await delay();
  const ctx = await loadCtx();
  const member = ctx.members.find((m) => m.id === memberId);
  if (!member) throw new ApiError("Member not found.", 404, "MEMBER_NOT_FOUND");

  const demandMap = new Map(ctx.demands.map((d) => [d.id, d]));
  const groupMap = new Map(ctx.groups.map((g) => [g.id, g]));
  const adjustments = MOCK_LEDGER_ADJUSTMENTS.filter((a) => a.memberId === memberId);
  const fees = await listLateFeeApplications();
  const receipts = ctx.receipts;
  const entries: LedgerEntry[] = [];

  const demandOf = (id: string) => demandMap.get(id);

  // 1. Installment due + dividend credit per decided cycle.
  for (const group of ctx.groups) {
    if (group.status !== "active" && group.status !== "completed") continue;
    const inGroup = group.tickets.some((t) => t.memberId === memberId);
    if (!inGroup) continue;
    for (const won of group.wonCycles) {
      const fin = cycleFinancials(group, won.cycle);
      if (!fin) continue;
      const period = cyclePeriodLabel(group.startDate, won.cycle);
      const dueDate = cycleDate(group.startDate, won.cycle, group.dueDay).toISOString();
      entries.push({
        id: `led-${group.id}-c${won.cycle}-${memberId}-due`,
        memberId,
        groupId: group.id,
        groupCode: group.groupCode,
        groupName: group.name,
        cycle: won.cycle,
        periodLabel: period,
        date: dueDate,
        type: "installment_due",
        narration: `Installment due for ${period}`,
        debit: group.monthlyInstallment,
        credit: 0,
        runningBalance: 0,
        reference: `Cycle ${won.cycle}`,
      });
      if (fin.dividend > 0) {
        entries.push({
          id: `led-${group.id}-c${won.cycle}-${memberId}-div`,
          memberId,
          groupId: group.id,
          groupCode: group.groupCode,
          groupName: group.name,
          cycle: won.cycle,
          periodLabel: period,
          date: dueDate,
          type: "dividend_credit",
          narration: `Dividend credit — ${period} auction discount`,
          debit: 0,
          credit: fin.dividend,
          runningBalance: 0,
          reference: `Cycle ${won.cycle}`,
        });
      }
    }
  }

  // 2. Payment received (linked to its receipt). Reversed receipts are marked.
  for (const r of receipts) {
    if (r.memberId !== memberId) continue;
    for (const p of r.payments) {
      const d = demandOf(p.demandId);
      const g = d ? groupMap.get(d.groupId) : null;
      entries.push({
        id: `led-${r.id}-${p.demandId}`,
        memberId,
        groupId: d?.groupId ?? "",
        groupCode: g?.groupCode ?? "—",
        groupName: g?.name ?? "—",
        cycle: d?.cycle ?? null,
        periodLabel: d?.periodLabel ?? null,
        date: r.receivedOn,
        type: "payment_received",
        narration: `Payment received${r.reversedAt ? " (reversed)" : ""} — ${d?.periodLabel ?? "collection"}`,
        debit: 0,
        credit: p.amount,
        runningBalance: 0,
        reference: r.receiptNumber,
        reversed: !!r.reversedAt,
      });
    }
  }

  // 3. Late fees (active and waived, marked when waived).
  for (const f of fees) {
    const d = demandOf(f.demandId);
    if (!d || d.memberId !== memberId) continue;
    const g = groupMap.get(d.groupId);
    entries.push({
      id: `led-fee-${f.id}`,
      memberId,
      groupId: d.groupId,
      groupCode: g?.groupCode ?? "—",
      groupName: g?.name ?? "—",
      cycle: d.cycle,
      periodLabel: d.periodLabel,
      date: f.appliedAt,
      type: "late_fee",
      narration: `Late fee${f.waivedAt ? " (waived)" : ""} — ${d.periodLabel}`,
      debit: f.amount,
      credit: 0,
      runningBalance: 0,
      reference: `Cycle ${d.cycle}`,
      reversed: !!f.waivedAt,
    });
  }

  // 4. Manual adjustments (always carry a reason).
  for (const a of adjustments) {
    const g = groupMap.get(a.groupId);
    const period = a.cycle != null ? cyclePeriodLabel(g?.startDate ?? "", a.cycle) : null;
    entries.push({
      id: `led-adj-${a.id}`,
      memberId,
      groupId: a.groupId,
      groupCode: g?.groupCode ?? "—",
      groupName: g?.name ?? "—",
      cycle: a.cycle,
      periodLabel: period,
      date: a.date,
      type: "adjustment",
      narration: a.narration,
      debit: a.kind === "debit" ? a.amount : 0,
      credit: a.kind === "credit" ? a.amount : 0,
      runningBalance: 0,
      reference: a.reference,
    });
  }

  // 5. Auction won & Payout disbursed.
  for (const payout of ctx.payouts) {
    if (payout.winnerMemberId !== memberId) continue;
    // Credit the won prize into member's account
    entries.push({
      id: `led-won-${payout.id}`,
      memberId,
      groupId: payout.groupId,
      groupCode: payout.groupCode,
      groupName: payout.groupName,
      cycle: payout.cycle,
      periodLabel: payout.periodLabel,
      date: payout.approval?.approvedAt ?? payout.createdAt,
      type: "auction_won",
      narration: `Auction won — ${payout.periodLabel} prize`,
      debit: 0,
      credit: payout.prize,
      runningBalance: 0,
      reference: `Cycle ${payout.cycle}`,
      reversed: false,
    });

    // Debit the actual disbursement paid out to member
    for (const dis of payout.disbursements) {
      entries.push({
        id: `led-pay-${payout.id}-${dis.id}`,
        memberId,
        groupId: payout.groupId,
        groupCode: payout.groupCode,
        groupName: payout.groupName,
        cycle: payout.cycle,
        periodLabel: payout.periodLabel,
        date: dis.paidOn,
        type: "payout_disbursed",
        narration: `Payout disbursed — ${payout.periodLabel} prize${payout.reversedAt ? " (reversed)" : ""}`,
        debit: dis.amount,
        credit: 0,
        runningBalance: 0,
        reference: payout.voucherNumber,
        reversed: !!payout.reversedAt,
      });
    }
  }

  // Sort by date then by type so a due always precedes its dividend on the same day.
  entries.sort((a, b) => (a.date === b.date ? TYPE_RANK[a.type] - TYPE_RANK[b.type] : a.date.localeCompare(b.date)));

  // Running contribution balance across all active entries.
  let balance = 0;
  for (const e of entries) {
    if (!e.reversed) {
      balance = Math.round((balance + e.debit - e.credit) * 100) / 100;
    }
    e.runningBalance = balance;
  }

  return entries;
}

/** Resolve the statement payout rows for a member in a group. */
function statementPayouts(ctx: LedgerCtx, memberId: string, groupId: string): StatementPayoutRow[] {
  return ctx.payouts
    .filter((p) => p.winnerMemberId === memberId && p.groupId === groupId)
    .map((p) => ({
      voucherNumber: p.voucherNumber,
      cycle: p.cycle,
      periodLabel: p.periodLabel,
      prize: p.prize,
      duesAdjusted: p.sameGroupDuesTotal,
      netReceived: p.amountPaid,
      paidOn: p.disbursements.length ? p.disbursements[p.disbursements.length - 1].paidOn : p.approval?.approvedAt ?? p.createdAt,
      status: p.status,
    }));
}

/** Build the group-wise statement for one member in one group (null if not a member). */
function buildGroupStatement(
  ctx: LedgerCtx,
  memberId: string,
  group: KametiGroup
): MemberStatementGroup | null {
  const ticket = group.tickets.find((t) => t.memberId === memberId);
  if (!ticket) return null;
  const member = ctx.members.find((m) => m.id === memberId);
  const memberDemands = ctx.demands.filter((d) => d.memberId === memberId && d.groupId === group.id);

  const cycles: StatementCycleRow[] = [];
  for (const won of [...group.wonCycles].sort((a, b) => a.cycle - b.cycle)) {
    const demand = memberDemands.find((d) => d.cycle === won.cycle);
    const period = cyclePeriodLabel(group.startDate, won.cycle);
    if (!demand) {
      cycles.push({
        cycle: won.cycle,
        periodLabel: period,
        baseInstallment: group.monthlyInstallment,
        dividendCredit: 0,
        netPayable: group.monthlyInstallment,
        paid: 0,
        balance: group.monthlyInstallment,
        paymentDate: null,
        receiptNumber: null,
        won: won.memberId === memberId,
        isReserved: won.cycle === group.reservedCycle,
      });
      continue;
    }
    // Latest non-reversed payment against this cycle's demand.
    const pay = findLatestPayment(ctx, demand.id);
    cycles.push({
      cycle: won.cycle,
      periodLabel: period,
      baseInstallment: demand.baseInstallment,
      dividendCredit: demand.dividendCredit,
      netPayable: demand.netPayable,
      paid: demand.paid,
      balance: demand.balance,
      paymentDate: pay?.receivedOn ?? null,
      receiptNumber: pay?.receiptNumber ?? null,
      won: demand.wonThisCycle,
      isReserved: won.cycle === group.reservedCycle,
    });
  }

  const totalCharged = cycles.reduce((s, c) => s + c.baseInstallment, 0);
  const totalDividend = cycles.reduce((s, c) => s + c.dividendCredit, 0);
  const totalPaid = cycles.reduce((s, c) => s + c.paid, 0);
  const totalOutstanding = cycles.reduce((s, c) => s + c.balance, 0);

  const wonCycle = group.wonCycles.find((w) => w.memberId === memberId)?.cycle ?? null;
  const wonCycleEntry = wonCycle != null ? group.wonCycles.find((w) => w.cycle === wonCycle) : null;
  const wonPayout = ctx.payouts.find((p) => p.groupId === group.id && p.cycle === wonCycle);
  const wonOn = wonPayout?.disbursements.length ? wonPayout.disbursements[wonPayout.disbursements.length - 1].paidOn : null;

  return {
    groupId: group.id,
    groupCode: group.groupCode,
    groupName: group.name,
    ticketNumber: ticket.ticketNumber,
    monthlyInstallment: group.monthlyInstallment,
    joinedOn: member?.enrollments.find((e) => e.groupId === group.id)?.joinedOn ?? group.startDate,
    wonCycle,
    wonOn,
    cycles,
    payouts: statementPayouts(ctx, memberId, group.id),
    totalCharged: Math.round(totalCharged * 100) / 100,
    totalDividend: Math.round(totalDividend * 100) / 100,
    totalPaid: Math.round(totalPaid * 100) / 100,
    totalOutstanding: Math.round(totalOutstanding * 100) / 100,
    cyclesRemaining: Math.max(0, group.memberCount - group.wonCycles.length),
  };
}

/** Latest non-reversed receipt payment against a demand. */
function findLatestPayment(ctx: LedgerCtx, demandId: string): { receivedOn: string; receiptNumber: string } | null {
  // Receipts are resolved lazily per call for freshness; we cache them on ctx elsewhere.
  return ctx.receipts.find((r) => r.payments.some((p) => p.demandId === demandId && !r.reversedAt)) ?? null;
}

/** Group-wise member statement. */
export async function getMemberStatement(memberId: string, groupId: string): Promise<MemberStatement> {
  if (!USE_MOCK_API) {
    const stmt = await http<BackendMemberStatement>(`/ledger/${memberId}/statement/${groupId}`, { method: "GET" });
    return {
      memberId: stmt.memberId,
      memberCode: stmt.memberCode,
      memberName: stmt.memberName,
      memberPhone: stmt.memberPhone,
      city: stmt.city,
      generatedAt: stmt.generatedAt,
      periodLabel: stmt.periodLabel,
      group: mapStatementGroup(stmt.group),
    };
  }

  await delay();
  const ctx = await loadCtx();
  const member = ctx.members.find((m) => m.id === memberId);
  if (!member) throw new ApiError("Member not found.", 404, "MEMBER_NOT_FOUND");
  const group = ctx.groups.find((g) => g.id === groupId);
  if (!group) throw new ApiError("Group not found.", 404, "GROUP_NOT_FOUND");
  const statement = buildGroupStatement(ctx, memberId, group);
  if (!statement) throw new ApiError("This member does not hold a ticket in this group.", 404, "NOT_IN_GROUP");
  return {
    memberId,
    memberCode: member.memberCode,
    memberName: member.fullName,
    memberPhone: member.primaryPhone,
    city: member.city,
    generatedAt: new Date().toISOString(),
    periodLabel: "Group-wise statement",
    group: statement,
  };
}

/** Consolidated statement — the member across all their groups on one page. */
export async function getMemberConsolidatedStatement(memberId: string): Promise<MemberConsolidatedStatement> {
  if (!USE_MOCK_API) {
    const stmt = await http<BackendMemberConsolidatedStatement>(`/ledger/${memberId}/statement`, { method: "GET" });
    return {
      memberId: stmt.memberId,
      memberCode: stmt.memberCode,
      memberName: stmt.memberName,
      memberPhone: stmt.memberPhone,
      city: stmt.city,
      generatedAt: stmt.generatedAt,
      groups: stmt.groups.map((g) => ({
        groupId: g.groupId,
        groupCode: g.groupCode,
        groupName: g.groupName,
        ticketNumber: g.ticketNumber,
        monthlyInstallment: Number(g.monthlyInstallment),
        totalCharged: Number(g.totalCharged),
        totalDividend: Number(g.totalDividend),
        totalPaid: Number(g.totalPaid),
        totalOutstanding: Number(g.totalOutstanding),
        wonCycle: g.wonCycle,
        cyclesRemaining: g.cyclesRemaining,
      })),
      totalOutstanding: Number(stmt.totalOutstanding),
      totalDividend: Number(stmt.totalDividend),
      payoutCount: stmt.payoutCount,
    };
  }

  await delay();
  const ctx = await loadCtx();
  const member = ctx.members.find((m) => m.id === memberId);
  if (!member) throw new ApiError("Member not found.", 404, "MEMBER_NOT_FOUND");

  const groups: ConsolidatedGroupSummary[] = [];
  for (const group of ctx.groups) {
    if (group.status !== "active" && group.status !== "completed") continue;
    const st = buildGroupStatement(ctx, memberId, group);
    if (!st) continue;
    groups.push({
      groupId: st.groupId,
      groupCode: st.groupCode,
      groupName: st.groupName,
      ticketNumber: st.ticketNumber,
      monthlyInstallment: st.monthlyInstallment,
      totalCharged: st.totalCharged,
      totalDividend: st.totalDividend,
      totalPaid: st.totalPaid,
      totalOutstanding: st.totalOutstanding,
      wonCycle: st.wonCycle,
      cyclesRemaining: st.cyclesRemaining,
    });
  }

  const totalOutstanding = groups.reduce((s, g) => s + g.totalOutstanding, 0);
  const totalDividend = groups.reduce((s, g) => s + g.totalDividend, 0);
  const payoutCount = ctx.payouts.filter((p) => p.winnerMemberId === memberId && p.disbursements.length > 0).length;

  return {
    memberId,
    memberCode: member.memberCode,
    memberName: member.fullName,
    memberPhone: member.primaryPhone,
    city: member.city,
    generatedAt: new Date().toISOString(),
    groups,
    totalOutstanding: Math.round(totalOutstanding * 100) / 100,
    totalDividend: Math.round(totalDividend * 100) / 100,
    payoutCount,
  };
}

export const ledgerService = {
  getMemberLedger,
  getMemberStatement,
  getMemberConsolidatedStatement,
};

export { ApiError };